import { useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import CustomModal from 'src/components/modal/customModal';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import Logger from 'src/lib/Logger';
import { axiosPostCall } from 'src/services/apisCall';

const logger = new Logger('bulkAssignCli');

const splitCliValues = (value) =>
  String(value || '')
    .split(/[\n,]+/)
    .map((cli) => cli.trim())
    .filter(Boolean);

const AddBulkassignCLi = ({ openModal, handleCloseModal, forceLoad }) => {
  const [canSubmit, setCanSubmit] = useState(true);

  const Schema = Yup.object().shape({
    assignToUserId: Yup.string().trim().required('User ID is required!'),
    clis: Yup.string()
      .trim()
      .required('CLI is required!')
      .test('has-cli', 'CLI is required!', (value) => splitCliValues(value).length > 0),
    action: Yup.string().required('Action is required!'),
    expiryDate: Yup.mixed().when('action', {
      is: 'assigned',
      then: (schema) => schema.required('Validity is required!'),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues: {
      assignToUserId: '',
      clis: '',
      action: 'assigned',
      expiryDate: null,
    },
  });

  const { control, watch, handleSubmit, reset, setValue } = methods;
  const action = watch('action');

  const resetForm = () => {
    reset({
      assignToUserId: '',
      clis: '',
      action: 'assigned',
      expiryDate: null,
    });
  };

  const handleClose = () => {
    resetForm();
    handleCloseModal();
  };

  const onSubmit = async (data) => {
    setCanSubmit(false);
    try {
      const cliList = splitCliValues(data?.clis);
      const payload = {
      assignToUserId: data?.assignToUserId.trim(),
      clis: cliList,
      action: data?.action,
    };
    console.log("🚀 ~ onSubmit ~ payload:", payload)

    if (data?.action === 'assigned') {
      payload.expiryDate = Number(data?.expiryDate);
    }
      const response = await axiosPostCall('assignRemoveVoiceClisFromUser', payload);
      renderSuccessMessage(response?.message);
      forceLoad(true);
      handleClose();
    } catch (error) {
      logger.error('Bulk Assign CLI [handleSubmit]', error);
      renderErrorMessage(error?.message);
    } finally {
      setCanSubmit(true);
    }
  };

  const description = (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
    <Box sx={{ p: '25px', width: '700px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'center' }}>
        <Typography sx={{ minWidth: '80px' }}>User ID</Typography>
        <RHFTextField name="assignToUserId" label="User ID"  placeholder="Enter User ID"/>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'center' }}>
        <Typography sx={{ minWidth: '80px' }}>CLIs</Typography>
        <RHFTextField
          name="clis"
          label="CLIs"
          placeholder="Enter CLIs separated by new lines or commas."
          multiline
          minRows={3}
          maxRows={6}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'center' }}>
        <Typography sx={{ minWidth: '80px' }}>Action</Typography>
        <RHFSelect
          name="action"
          label="Action"
          size="small"
          onChange={(e) => {
            setValue('action', e.target.value);
            if (e.target.value === 'remove') setValue('expiryDate', null);
          }}
          sx={{ width: '180px' }}
        >
          <MenuItem value="assigned">Assign</MenuItem>
          <MenuItem value="removed">Removed</MenuItem>
        </RHFSelect>
      </Box>

      {action === 'assigned' && (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'center' }}>
          <Typography sx={{ minWidth: '80px' }}>Validity</Typography>
          <Controller
            name="expiryDate"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Validity"
                  value={field.value ? dayjs.unix(field.value) : null}
                  onChange={(newValue) =>
                    field.onChange(newValue ? newValue.startOf('day').unix() : null)
                  }
                  shouldDisableDate={(date) => dayjs(date).isBefore(dayjs().startOf('day'))}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!error,
                      helperText: error?.message,
                      sx: {
                        width: '180px',
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            )}
            />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: '1rem', mt: 1 }}>
        <Box
          sx={{
            width: '7.625rem',
            backgroundColor: canSubmit ? '#212B36' : 'grey',
            borderRadius: '8px',
            p: '0.562rem',
            height: '2.375rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pointerEvents: canSubmit ? 'auto' : 'none',
          }}
          onClick={handleSubmit(onSubmit)}
        >
          <Typography sx={{ fontWeight: 600, color: '#fff' }}>Confirm</Typography>
        </Box>
        <Button type="button" onClick={handleClose}>Cancel</Button>
      </Box>
    </Box>
    </FormProvider>
  );

  return (
    openModal && (
      <CustomModal
        open={openModal}
        title="Bulk Assign/Remove CLI to User"
        description={description}
        close={handleClose}
        isCloseIcon={true}
      />
    )
  );
};

export default AddBulkassignCLi;
